import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Field, Input, Select, Textarea } from "@sagot/ui";
import { useState } from "react";
import { bodyOf, EMPTY_FORM, formOf, type CaseForm, type FormProblem, type RegressionCase, type RegressionCaseBody } from "./regression-model";

const R = "engage.workflows.regression";

interface CaseFormDialogProps {
  /** The case being edited; `null` adds a new one. */
  editing: RegressionCase | null;
  busy: boolean;
  /** The server's refusal, already worded. */
  error: string | null;
  onSubmit: (body: RegressionCaseBody) => void;
  onClose: () => void;
}

/** One test case: the customer's messages and facts in, what the flow must do out. Checked here, then by the server. */
export function CaseFormDialog({ editing, busy, error, onSubmit, onClose }: CaseFormDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CaseForm>(editing ? formOf(editing) : EMPTY_FORM);
  const [problem, setProblem] = useState<FormProblem | null>(null);
  const set = <K extends keyof CaseForm>(key: K, value: CaseForm[K]) => { setForm((before) => ({ ...before, [key]: value })); setProblem(null); };
  const submit = () => {
    const result = bodyOf(form);
    if (result.ok) onSubmit(result.body);
    else setProblem(result.problem);
  };
  const failed = (which: FormProblem) => (problem === which ? t(`${R}.form.problems.${which}`) : undefined);
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(editing ? `${R}.form.editTitle` : `${R}.form.addTitle`)}
        description={t(`${R}.form.description`)}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t(`${R}.form.cancel`)}</Button>
            <Button loading={busy} onClick={submit}>{t(`${R}.form.save`)}</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); submit(); }} noValidate>
          <Field label={t(`${R}.form.name`)} error={failed("name")}>
            <Input value={form.name} maxLength={120} onChange={(event) => { set("name", event.target.value); }} />
          </Field>
          <Field label={t(`${R}.form.messages`)} hint={t(`${R}.form.messagesHint`)} error={failed("messages")}>
            <Textarea rows={3} value={form.messages} onChange={(event) => { set("messages", event.target.value); }} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t(`${R}.form.tier`)}>
              <Input value={form.tier} maxLength={120} onChange={(event) => { set("tier", event.target.value); }} />
            </Field>
            <Field label={t(`${R}.form.status`)}>
              <Input value={form.status} maxLength={120} onChange={(event) => { set("status", event.target.value); }} />
            </Field>
          </div>
          <Field label={t(`${R}.form.attributes`)} hint={t(`${R}.form.attributesHint`)} error={failed("attributes")}>
            <Textarea rows={2} value={form.attributes} onChange={(event) => { set("attributes", event.target.value); }} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t(`${R}.form.path`)} hint={t(`${R}.form.pathHint`)}>
              <Input value={form.path} maxLength={64} onChange={(event) => { set("path", event.target.value); }} />
            </Field>
            <Field label={t(`${R}.form.handover`)}>
              <Select value={form.handover} onChange={(event) => { set("handover", event.target.value === "yes" ? "yes" : event.target.value === "no" ? "no" : "any"); }}>
                <option value="any">{t(`${R}.form.handoverAny`)}</option>
                <option value="yes">{t(`${R}.form.handoverYes`)}</option>
                <option value="no">{t(`${R}.form.handoverNo`)}</option>
              </Select>
            </Field>
          </div>
          <Field label={t(`${R}.form.contains`)} error={failed("expected")}>
            <Textarea rows={2} value={form.contains} onChange={(event) => { set("contains", event.target.value); }} />
          </Field>
          {error ? <p role="alert" className="text-xs font-semibold text-danger">{error}</p> : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
